import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { getData } from "../../../FetchApiServices";

import osifMark    from "../../../assets/plot-os/osif.svg";
import odifMark    from "../../../assets/plot-os/odif.svg";
import bscMark     from "../../../assets/plot-os/bsc.svg";
import t2tMark     from "../../../assets/plot-os/t2t.svg";
import pmsMark     from "../../../assets/plot-os/plot-pms.svg";
import projectMark from "../../../assets/plot-os/plot-project.svg";
import aerialMark  from "../../../assets/plot-os/aerial.svg";

// Bundled product marks, keyed by registry slug. The switcher doc (§5.2) says
// to render logo_url with an initials fallback; we prefer a local mark when we
// have one so the panel looks right before the registry's logo_url fields are
// filled in (§6.2 lists them as still-pending hygiene). Order of preference:
// bundled mark -> logo_url -> initials on the product's accent colour.
const LOCAL_MARKS = {
  osif:           osifMark,
  odif:           odifMark,
  bsc:            bscMark,
  t2t:            t2tMark,
  "plot-pms":     pmsMark,
  "plot-project": projectMark,
  aerial:         aerialMark,
};

// Display overrides, keyed by registry slug. The central PLOT OS registry is
// the source of truth for names/accents, but a couple of them still read as the
// old internal codenames ("AERIAL", "PLOT Project") and the registry accents
// don't match the brand colours the PLOT OS console renders. Until the registry
// is corrected centrally we override the display here so the switcher matches
// the console: `name` renames the tile, `color` paints the tile label in the
// product's brand colour. Fall back to the registry name/accent for any slug we
// don't list.
const DISPLAY = {
  t2t:            { color: "#F38A04" },                       // hourglass orange
  osif:           { color: "#28A828" },                       // OSIF green
  odif:           { color: "#1F9E3B" },                       // ODIF green
  "plot-project": { name: "PROJECT",     color: "#D0130D" },  // PROJECT red
  "plot-pms":     { color: "#6A3FC0" },                       // PMS purple
  bsc:            { color: "#DF4C07" },                       // BSC orange
  aerial:         { name: "JINDAL WA'I", color: "#111111" },  // near-black
};

// Our own slug — the tile for this product is badged "Current" and links home
// instead of going through /plot/switch (there is nothing to hand off to).
const CURRENT_SLUG = "t2t";
const HOME_PATH = "/t2t-home";

const HUB_URL = "https://os.plotwbs.com";

const initialsOf = (name = "") =>
  name.split(/[\s-]+/).map((w) => w[0]).join("").toUpperCase().slice(0, 3);

const ProductTile = ({ product, onNavigate }) => {
  const { slug, name, accent, logo_url: logoUrl, has_access: hasAccess } = product;
  const isCurrent = slug === CURRENT_SLUG;
  const mark = LOCAL_MARKS[slug] || logoUrl || null;
  const navigate = useNavigate();
  const location = useLocation();

  // Display name/colour: our override wins, else the registry's own values.
  const override    = DISPLAY[slug] || {};
  const displayName = override.name || name;
  const brandColor  = override.color || accent || undefined;

  const art = mark ? (
    <img src={mark} alt="" className="t2t-ps-mark" />
  ) : (
    <span className="t2t-ps-mark t2t-ps-mark-initials" style={{ background: brandColor || "#64748B" }}>
      {initialsOf(displayName)}
    </span>
  );

  const body = (
    <>
      <span className="t2t-ps-art">{art}</span>
      {/* Brand colour only on tiles you can open; a no-access tile keeps the
          muted grey name from .is-locked (§5.2's greying is the access cue). */}
      <span className="t2t-ps-name" style={hasAccess ? { color: brandColor } : undefined}>{displayName}</span>
      {isCurrent ? (
        <span className="t2t-ps-state t2t-ps-state-current">Current</span>
      ) : hasAccess ? (
        <span className="t2t-ps-state t2t-ps-state-open">Open</span>
      ) : (
        <span className="t2t-ps-state t2t-ps-state-locked">
          <i className="bi bi-lock-fill" /> No access
        </span>
      )}
    </>
  );

  // Our own tile is a link home: from anywhere in T2T it opens the home page,
  // and from the home page it reloads it. A tile that swallowed the click read
  // as broken. Kept as an <a> so ctrl/middle-click still opens a new tab; the
  // plain click is handled in-app so we don't re-bootstrap the whole SPA.
  if (isCurrent) {
    const goHome = (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (onNavigate) onNavigate();
      if (location.pathname === HOME_PATH) window.location.reload();
      else navigate(HOME_PATH);
    };
    return (
      <a
        className="t2t-ps-tile is-current"
        href={HOME_PATH}
        onClick={goHome}
        title={`${displayName} — you are here; click to open the home page`}
      >
        {body}
      </a>
    );
  }

  // A no-access tile is NOT a link -- there is nothing to click through (§5.2).
  // The greying is presentation only; /plot/switch is the actual gate (§8.5).
  if (!hasAccess) {
    return (
      <div
        className="t2t-ps-tile is-locked"
        title="You don't have access to this application — contact your administrator to request it."
      >
        {body}
      </div>
    );
  }

  // A real <a>, so middle-click / ctrl-click "open in new tab" works for free,
  // and the whole handoff stays a top-level navigation (§5.1).
  return (
    <a className="t2t-ps-tile" href={`/plot/switch?app=${encodeURIComponent(slug)}`}>
      {body}
    </a>
  );
};

ProductTile.propTypes = {
  product: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
};

const ProductSwitcher = ({ onOpenChange }) => {
  const [open, setOpen]         = useState(false);
  const [state, setState]       = useState("idle"); // idle | loading | ready | error
  const [products, setProducts] = useState([]);
  const [pos, setPos]           = useState({ top: 0, left: 0 });
  const triggerRef              = useRef(null);
  const panelRef                = useRef(null);

  // The sidebar is an overlay with overflow-y:auto, which would clip a panel
  // wider than its 220px -- and its onMouseLeave closes it, which would tear
  // the panel down as the pointer travelled to it. So the panel is portalled
  // to <body> and positioned off the trigger, and the sidebar is told to stop
  // auto-closing while it is open (onOpenChange).
  const setOpenAndNotify = useCallback((next) => {
    setOpen(next);
    if (onOpenChange) onOpenChange(next);
  }, [onOpenChange]);

  // Fetch on EVERY open, never on page load (§5.2). The ~5-minute cache lives
  // in the backend proxy only -- that is what makes a server-side eviction
  // actually change what the next open renders (§9).
  const load = useCallback(async () => {
    setState("loading");
    const resp = await getData("plot/products");

    if (resp.error) {
      // account_disabled is the central kill switch: end the local session
      // exactly as the mint path does (§9).
      if (resp.error.status === 403) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/?sso_error=account_disabled";
        return;
      }
      setState("error"); // never blank, never a raw error, never cached
      return;
    }

    const rows = resp.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) {
      setState("error"); // §4.2: an authenticated 200 always has our own row
      return;
    }
    setProducts(rows);
    setState("ready");
  }, []);

  const toggle = () => {
    const next = !open;
    if (next) {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) {
        const width = 320;
        setPos({
          top: r.bottom + 8,
          left: Math.max(8, Math.min(r.left, window.innerWidth - width - 12)),
        });
      }
      load();
    }
    setOpenAndNotify(next);
  };

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPanel   = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpenAndNotify(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenAndNotify(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpenAndNotify]);

  const others     = products.filter((p) => p.slug !== CURRENT_SLUG);
  const allGreyed  = others.length > 0 && others.every((p) => !p.has_access);

  return (
    <div className="t2t-ps">
      <button
        type="button"
        ref={triggerRef}
        className="t2t-ps-trigger"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        title="PLOT OS applications"
      >
        <i className="bi bi-grid-3x3-gap-fill" />
        <span className="t2t-ps-trigger-label">PLOT OS</span>
      </button>

      {open && createPortal(
        <div
          className="t2t-ps-panel"
          role="menu"
          ref={panelRef}
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="t2t-ps-title">PLOT OS APPLICATIONS</div>

          {state === "loading" && (
            <div className="t2t-ps-msg">Loading applications…</div>
          )}

          {state === "error" && (
            <div className="t2t-ps-msg">
              Couldn&apos;t load — try again shortly.
              <button type="button" className="t2t-ps-retry" onClick={load}>Retry</button>
            </div>
          )}

          {state === "ready" && (
            <>
              <div className="t2t-ps-grid">
                {products.map((p) => (
                  <ProductTile
                    key={p.slug}
                    product={p}
                    onNavigate={() => setOpenAndNotify(false)}
                  />
                ))}
              </div>
              {allGreyed && (
                <div className="t2t-ps-msg t2t-ps-msg-quiet">
                  You don&apos;t have access to any other applications yet — contact your administrator.
                </div>
              )}
            </>
          )}

          <a className="t2t-ps-console" href={HUB_URL} target="_blank" rel="noreferrer">
            PLOT OS console
          </a>
        </div>,
        document.body
      )}
    </div>
  );
};

ProductSwitcher.propTypes = { onOpenChange: PropTypes.func };

export default ProductSwitcher;
